import React from 'react';
import { colors, fonts, sectionLabelStyle } from '../../patientintel/styles';

// Visual treatment mirrors patientintel/GoDeeperSection. Copy is sourced
// from GSK_COPY_FOR_REVIEW.md.

const ModeBadge = ({ mode }) => {
  const isLive = mode === 'Live';
  const isAsync = mode === 'Async';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontFamily: fonts.sans,
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        color: colors.deepCoral,
        marginBottom: '10px',
      }}
    >
      {isLive && (
        <span
          aria-hidden="true"
          className="gsk-live-dot"
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: colors.terracotta,
            boxShadow: `0 0 0 0 ${colors.terracotta}`,
            display: 'inline-block',
          }}
        />
      )}
      {isAsync && (
        <span aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ width: '12px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
          <span style={{ width: '8px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
          <span style={{ width: '10px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
        </span>
      )}
      {!isLive && !isAsync && (
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: colors.terracotta }} />
          <span style={{ width: '1px', height: '8px', backgroundColor: colors.coralMid }} />
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '1.5px' }}>
            <span style={{ width: '8px', height: '1.5px', backgroundColor: colors.coral }} />
            <span style={{ width: '6px', height: '1.5px', backgroundColor: colors.coral }} />
          </span>
        </span>
      )}
      {mode}
    </span>
  );
};

const methods = [
  {
    mode: 'Live',
    title: 'In-Depth Interviews',
    desc: 'One-on-one conversations surfacing rich lived experience and the nuanced language patients use to describe their condition, treatment, and care journey.',
    uses: ['Lived experience', 'unmet needs', 'exploratory conversations'],
  },
  {
    mode: 'Live',
    title: 'Focus Groups & Advisory Boards',
    desc: 'Group discussions surfacing shared experiences, areas of divergence, and collective patient priorities — sourced, facilitated, and reported by our team.',
    uses: ['Community norms', 'comparative perspectives', 'co-design'],
  },
  {
    mode: 'Async',
    title: 'Protocol Reviews',
    desc: 'Structured patient review of trial design, schedules, burden of participation, and informed consent — flagging barriers before recruitment begins.',
    uses: ['De-risking trial design', 'inclusion criteria', 'identifying barriers'],
  },
  {
    mode: 'Async',
    title: 'Activity Boards',
    desc: "Structured asynchronous platform activities capturing qualitative depth at scale, on participants' own schedules — without requiring synchronous availability.",
    uses: ['Recruitment materials', 'concept validation', 'patient materials'],
  },
  {
    mode: 'Async',
    title: 'Surveys',
    desc: 'Robust quantitative and qualitative surveys designed for larger patient cohorts — statistically meaningful at scale, with verified participants throughout.',
    uses: ['Population-level insight', 'PRO testing', 'broad pattern analysis'],
  },
  {
    mode: 'Live or Async',
    title: 'Concept & Materials Review',
    desc: 'Patients review prototypes, communications, educational content, or study materials and provide structured, actionable feedback.',
    uses: ['Message testing', 'patient materials', 'prototype feedback'],
  },
];

export const MethodsSection = () => (
  <section
    style={{
      backgroundColor: colors.white,
      padding: '96px 48px',
      boxSizing: 'border-box',
    }}
  >
    <style>{`
      @media (max-width: 768px) {
        .gsk-methods-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
        .gsk-methods-grid { grid-template-columns: 1fr !important; }
      }
      @keyframes gsk-pulse {
        0% { box-shadow: 0 0 0 0 rgba(232,146,124,0.55); }
        70% { box-shadow: 0 0 0 10px rgba(232,146,124,0); }
        100% { box-shadow: 0 0 0 0 rgba(232,146,124,0); }
      }
      .gsk-live-dot { animation: gsk-pulse 2s infinite; }
      .gsk-method-card { transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s; }
      .gsk-method-card:hover { border-color: ${colors.coralMid} !important; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(235,160,155,0.18); }
    `}</style>
    <div className="gsk-methods-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center' }}>
        <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>Our Research Methods</p>
        <h2
          style={{
            fontFamily: fonts.serif,
            fontSize: 'clamp(26px, 3vw, 40px)',
            fontWeight: 700,
            color: colors.charcoal,
            margin: '0 0 16px',
            lineHeight: 1.2,
          }}
        >
          Fit-for-purpose methods,
          <br />
          <span style={{ fontStyle: 'italic', fontWeight: 400 }}>tailored for your objective.</span>
        </h2>
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '17px',
            color: colors.warmGray,
            maxWidth: '640px',
            margin: '0 auto',
            fontWeight: 400,
            lineHeight: 1.75,
            textWrap: 'pretty',
          }}
        >
          We bring the full range of qualitative and quantitative research methods to patient engagement — led by our in-house research team with the rigor pharma requires. No one method fits every question. We design for your objective.
        </p>
      </div>

      <div
        className="gsk-methods-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '16px',
          maxWidth: '960px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {methods.map((m) => (
          <div
            key={m.title}
            className="gsk-method-card"
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              padding: '24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <ModeBadge mode={m.mode} />
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: '16px',
                fontWeight: 600,
                color: colors.charcoal,
                marginBottom: '10px',
                lineHeight: 1.3,
              }}
            >
              {m.title}
            </div>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: '14px',
                color: colors.warmGray,
                lineHeight: 1.6,
                fontWeight: 400,
                marginBottom: '14px',
                textWrap: 'pretty',
              }}
            >
              {m.desc}
            </div>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: '12px',
                color: colors.midGray,
                fontStyle: 'italic',
                lineHeight: 1.55,
                marginTop: 'auto',
              }}
            >
              {m.uses.map((u, i) => (
                <React.Fragment key={u}>
                  {i > 0 && ' · '}
                  <span style={{ whiteSpace: 'nowrap' }}>{u}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
